// Totals for the Daily Sales Report (thermal / IMIN / DSR) - computed ONLY from
// what each invoice stored when it was made, never from today's item master:
//   - line price     = the line's saved `rate` (the price actually charged),
//   - invoice amount = the invoice's saved `total` (what the customer paid),
//   - discount / VAT / round-off / received = the invoice's saved values.
// (The category label of a line still comes from the item, because an invoice
// line does not store the category - an item moved to another category later
// moves its old sales with it.)

import { formatInvoiceNumber } from "./docNumber";

const num = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const lineRate = (line) => num(line?.rate ?? line?.overridePrice ?? line?.originalRate ?? 0);
const lineQty = (line) => num(line?.quantity ?? line?.qty ?? 0);
const hasRate = (value) => value !== undefined && value !== null && value !== "" && Number.isFinite(parseFloat(value));

/**
 * Was this invoice's VAT added ON TOP of the line amounts, or already INSIDE them?
 *   exclusive:  total = lines - discount + vat   -> the VAT is an extra charge
 *   inclusive:  total = lines - discount          -> the VAT is part of the price
 * Decided from the saved numbers, so a store that switched between inclusive and
 * exclusive keeps each old invoice correct.
 */
const vatIsAdded = (gross, discount, vat, total, roundOff) => {
  if (!(vat > 0)) return false;
  const tolerance = Math.abs(roundOff) + 0.51;
  const addedGap = Math.abs(gross - discount + vat - total);
  const includedGap = Math.abs(gross - discount - total);
  return addedGap <= includedGap && addedGap <= tolerance;
};

// Splits one invoice's VAT over the VAT rates saved on its lines, so the report can
// show taxable amount + VAT per rate. The invoice's own saved VAT stays the truth:
// each line's theoretical VAT is only used as the share it gets of that amount.
// Invoices without a saved rate on every line go to the "unknown" bucket.
const splitVatByRate = (invoice, lineAmounts, gross, discount, vat, vatAddedOnTop, bucket) => {
  const lines = invoice.items || [];
  const base = gross > 0 ? 1 - discount / gross : 1; // discount spread pro-rata over the lines
  const net = lineAmounts.map((amount) => amount * base);
  const netTotal = net.reduce((a, b) => a + b, 0);
  const add = (key, taxable, vatAmount) => {
    const row = bucket.get(key) || { rate: key === "unknown" ? null : Number(key), taxable: 0, vat: 0 };
    row.taxable += taxable;
    row.vat += vatAmount;
    bucket.set(key, row);
  };

  const allRated = lines.length > 0 && lines.every((line) => hasRate(line?.vatRate));
  if (!(vat > 0)) {
    // No VAT on the invoice: everything is untaxed ("0%"), whatever the items' own rates say.
    add("0", netTotal, 0);
    return;
  }
  if (!allRated) {
    add("unknown", vatAddedOnTop ? netTotal : netTotal - vat, vat);
    return;
  }
  const theory = lines.map((line, i) => {
    const rate = num(line.vatRate);
    return vatAddedOnTop ? (net[i] * rate) / 100 : (net[i] * rate) / (100 + rate);
  });
  const theoryTotal = theory.reduce((a, b) => a + b, 0);
  if (!(theoryTotal > 0)) {
    add("unknown", vatAddedOnTop ? netTotal : netTotal - vat, vat);
    return;
  }
  const scale = vat / theoryTotal;
  lines.forEach((line, i) => {
    const vatShare = theory[i] * scale;
    add(String(num(line.vatRate)), vatAddedOnTop ? net[i] : net[i] - vatShare, vatShare);
  });
};

export function computeSalesTotals(invoices) {
  const categoryTotals = {};
  const categoryQty = {};
  const paymentTotals = {};
  const paymentCounts = {};
  const itemTotals = new Map();
  const vatBucket = new Map();
  let totalSales = 0;
  let totalDiscount = 0;
  let discountCount = 0;
  let vatAdded = 0;
  let vatIncluded = 0;
  let roundOffTotal = 0;
  let netSales = 0;
  let itemsSold = 0;
  let creditDue = 0;
  let creditCount = 0;
  const numbers = [];

  (invoices || []).forEach((invoice) => {
    let gross = 0;
    const lineAmounts = [];
    (invoice.items || []).forEach((line) => {
      // Lines whose item no longer resolves to a category go to "Uncategorized"
      // instead of dropping the invoice from the totals.
      const category = line?.item?.category?.categoryName || "Uncategorized";
      const qty = lineQty(line);
      const amount = qty * lineRate(line);
      categoryTotals[category] = (categoryTotals[category] || 0) + amount;
      categoryQty[category] = (categoryQty[category] || 0) + qty;
      gross += amount;
      itemsSold += qty;
      lineAmounts.push(amount);

      const key = String(line?.item?._id || line?.item || line?._id || "?");
      const entry = itemTotals.get(key) || { name: line?.item?.itemName || "Unknown item", qty: 0, amount: 0 };
      entry.qty += qty;
      entry.amount += amount;
      itemTotals.set(key, entry);
    });

    const discount = num(invoice.discount);
    const vat = num(invoice.vat);
    const roundOff = num(invoice.roundOff);
    const total = num(invoice.total);

    const mode = invoice.paymentMode || "Other";
    paymentTotals[mode] = (paymentTotals[mode] || 0) + total;
    paymentCounts[mode] = (paymentCounts[mode] || 0) + 1;

    totalSales += gross;
    totalDiscount += discount;
    if (discount > 0) discountCount += 1;
    roundOffTotal += roundOff;
    netSales += total;
    const onTop = vat > 0 && vatIsAdded(gross, discount, vat, total, roundOff);
    if (vat > 0) {
      if (onTop) vatAdded += vat;
      else vatIncluded += vat;
    }
    splitVatByRate(invoice, lineAmounts, gross, discount, vat, onTop, vatBucket);

    // Credit: what is STILL unpaid on an invoice that was not settled in full at the till.
    const status = String(invoice.status || "");
    if (status && status.toLowerCase() !== "paid") {
      const due = Math.max(0, total - num(invoice.totalAmountReceived));
      if (due > 0.004) {
        creditDue += due;
        creditCount += 1;
      }
    }

    const asNumber = parseInt(invoice.invoiceNumber, 10);
    if (Number.isFinite(asNumber)) numbers.push(asNumber);
  });

  const count = (invoices || []).length;
  const categoryList = Object.keys(categoryTotals)
    .map((name) => ({ name, qty: categoryQty[name] || 0, amount: categoryTotals[name] }))
    .sort((a, b) => b.amount - a.amount);
  const topItems = [...itemTotals.values()].sort((a, b) => b.amount - a.amount).slice(0, 10);
  const vatByRate = [...vatBucket.values()]
    .filter((row) => row.taxable > 0.004 || row.vat > 0.004)
    .sort((a, b) => (b.rate ?? -1) - (a.rate ?? -1));

  return {
    categoryTotals,
    paymentTotals,
    totalSales,
    totalDiscount,
    vatAdded,
    vatIncluded,
    // Every VAT amount recorded on the invoices (added + included) - kept for callers
    // that still show one "Total VAT" figure.
    totalVat: vatAdded + vatIncluded,
    roundOffTotal,
    // Net sales = what was actually collected: the sum of the invoices' saved totals.
    // This equals the sum of the payment-mode lines, so the report always agrees with itself.
    netSales,

    // --- counts ---
    invoiceCount: count,
    itemsSold,
    averageBill: count ? netSales / count : 0,
    discountCount,
    firstInvoice: numbers.length ? formatInvoiceNumber(String(Math.min(...numbers))) : "",
    lastInvoice: numbers.length ? formatInvoiceNumber(String(Math.max(...numbers))) : "",
    paymentCounts,

    // --- collected vs credit ---
    creditDue,
    creditCount,
    collectedAtSale: netSales - creditDue,

    // --- breakdowns ---
    categoryList,
    topItems,
    vatByRate,
  };
}

// Day extras (from GET /invoices/daily-summary-extras) rolled into the figures the
// report prints. `extras` may be null/partial (not loaded yet, or the call failed).
export function summariseDayExtras(extras) {
  const returns = extras?.returns || [];
  const deleted = extras?.deletedInvoices || [];
  const received = extras?.paymentsReceived || [];
  const receivedByMode = {};
  received.forEach((payment) => {
    const mode = payment.paymentMethod || "Other";
    receivedByMode[mode] = (receivedByMode[mode] || 0) + num(payment.amountPaid);
  });
  return {
    loaded: !!extras,
    returnsCount: returns.length,
    returnsTotal: returns.reduce((sum, r) => sum + num(r.totalReturnAmount), 0),
    deletedCount: deleted.length,
    deletedTotal: deleted.reduce((sum, d) => sum + num(d.total), 0),
    receivedCount: received.length,
    receivedTotal: received.reduce((sum, p) => sum + num(p.amountPaid), 0),
    receivedByMode,
  };
}
